// CompanyDetailsPanel.jsx
import React, { useState, useEffect } from "react";

import { FaEdit, FaTrash, FaGlobe, FaMapMarkerAlt, FaUsers, FaBriefcase, FaBuilding } from "react-icons/fa";
import { Plus } from "lucide-react";
import { useAuth } from "../auth/AuthContext";
import UiText from '../components/UiText';
import AddActivityModal from '../components/AddActivityModal';
import RecentActivityTimeline from '../components/RecentActivityTimeline';
import api from '../api/axios';


export default function CompanyDetailsPanel({
  company,
  onViewCustomers,
  onViewDeals,
  onEdit,
  onDelete,
  onAddDeal,
  onAddNote,
}) {
  const { user } = useAuth();
  const [showActivityModal, setShowActivityModal] = useState(false);
  const [activities, setActivities] = useState([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [activeTab, setActiveTab] = useState("details"); // "details" or "activity"
  const [editingActivity, setEditingActivity] = useState(null);
  const [activityError, setActivityError] = useState("");

  // Helper function to check if user can edit/delete the company
  const canEditCompany = (comp) => {
    if (!user) return false;
    // Only admin can edit any company. Executives can edit only companies they created.
    if (user.role === "admin") return true;
    if (comp.owner_id && String(user.id) === String(comp.owner_id)) return true;
    return false;
  };

  // Fetch activities when company changes
  useEffect(() => {
    if (company?.id) {
      fetchActivities();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id]);

  const fetchActivities = async () => {
    if (!company?.id) return;
    
    setLoadingActivities(true);
    try {
      const response = await api.get(`/api/companies/${company.id}/activities/`);
      setActivities(response.data);
    } catch (error) {
      console.error("Error fetching activities:", error);
      // If endpoint doesn't exist yet, just set empty array
      setActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleSaveActivity = async (activityData) => {
    setActivityError("");
    try {
      if (editingActivity?.id) {
        console.log("Updating activity with data:", activityData);
        const response = await api.patch(
          `/api/companies/${company.id}/activities/${editingActivity.id}`,
          activityData
        );
        console.log("Activity updated successfully:", response.data);
        setActivities(
          activities.map((act) =>
            act.id === response.data.id ? response.data : act
          )
        );
      } else {
        console.log("Adding activity with data:", {
          ...activityData,
          company: company.id,
        });

        const response = await api.post(`/api/companies/${company.id}/activities/`, {
          ...activityData,
          company: company.id,
        });

        console.log("Activity created successfully:", response.data);
        setActivities([response.data, ...activities]);
      }

      setEditingActivity(null);
      setShowActivityModal(false);
    } catch (error) {
      console.error("Full error object:", error);
      console.error("Error response:", error.response);
      console.error("Error message:", error.message);

      const errorMessage = error.response?.data?.detail || 
                          error.response?.data?.message || 
                          error.message || 
                          "Failed to save activity";

      setActivityError(errorMessage);
      setTimeout(() => setActivityError(""), 5000);
    }
  };

  const handleEditActivity = (activity) => {
    setEditingActivity(activity);
    setShowActivityModal(true);
  };

  if (!company) {
    return (
      <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center", 
        justifyContent: "center",
        height: "100%",
        minHeight: "400px",
        textAlign: "center",
        padding: "2rem"
      }}>
        <div style={{ 
          fontSize: "80px", 
          opacity: "0.15", 
          marginBottom: "1.5rem",
          background: "#2563eb",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}>🏢</div>
        <UiText variant="heading" as="h2" style={{ 
          color: "#374151", 
          marginBottom: "0.75rem",
          fontSize: "1.25rem",
          fontWeight: "600"
        }}>
          No Company Selected
        </UiText>
        <UiText variant="small" as="p" style={{ 
          color: "#9ca3af", 
          lineHeight: "1.6",
          maxWidth: "250px"
        }}>
          Select a company from the table to view detailed information
        </UiText>
      </div>
    );
  }

  // ✅ new helpers (no UI change, just click behavior + accessibility)
  const handleCustomersClick = () => {
    if (onViewCustomers) onViewCustomers(company);
  };

  const handleDealsClick = () => {
    if (onViewDeals) onViewDeals(company);
  };


  return (
    <div style={{ 
      display: "flex", 
      flexDirection: "column", 
      gap: "0.75rem",
      height: "100%",
      maxHeight: "calc(100vh - 200px)"
    }}>
      {/* Activity Error Message */}
      {activityError && (
        <div style={{
          padding: "0.75rem",
          backgroundColor: "#fee2e2",
          border: "1px solid #fecaca",
          borderRadius: "6px",
          color: "#991b1b",
          fontSize: "0.875rem"
        }}>
          ⚠️ {activityError}
        </div>
      )}
      {/* Header Section */}
      <div style={{ 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center",
        paddingBottom: "0.75rem",
        borderBottom: "2px solid #f3f4f6"
      }}>
        <div style={{ 
          position: "relative",
          marginBottom: "0.5rem"
        }}>
          <div style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            border: "3px solid #e0e7ff",
            boxShadow: "0 4px 12px rgba(99, 102, 241, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#2563eb",
            fontSize: "36px"
          }}>
            🏢
          </div>
        </div>
        <UiText variant="title" as="h2" style={{ 
          fontSize: "1.25rem", 
          marginBottom: "0.125rem",
          color: "#111827",
          fontWeight: "700",
          textAlign: "center"
        }}>
          {company.name}
        </UiText>
        <UiText variant="small" as="p" style={{ 
          color: "#6b7280",
          fontSize: "0.875rem",
          marginBottom: "0.375rem",
          textAlign: "center"
        }}>
          {company.industry || "No industry"}
        </UiText>
      </div>

      {/* Tabs */}
      <div style={{ 
        display: "flex", 
        borderBottom: "2px solid #f3f4f6",
        gap: "1rem",
        paddingBottom: "0"
      }}>
        <button
          onClick={() => setActiveTab("details")}
          style={{
            background: "none",
            border: "none",
            padding: "0.75rem 1rem",
            fontSize: "0.875rem",
            fontWeight: "600",
            cursor: "pointer",
            color: activeTab === "details" ? "#2563eb" : "#6b7280",
            borderBottom: activeTab === "details" ? "3px solid #2563eb" : "3px solid transparent",
            transition: "all 0.2s",
            marginBottom: "-2px"
          }}
        >
          Details
        </button>
        <button
          onClick={() => setActiveTab("activity")}
          style={{
            background: "none",
            border: "none",
            padding: "0.75rem 1rem",
            fontSize: "0.875rem",
            fontWeight: "600",
            cursor: "pointer",
            color: activeTab === "activity" ? "#2563eb" : "#6b7280",
            borderBottom: activeTab === "activity" ? "3px solid #2563eb" : "3px solid transparent",
            transition: "all 0.2s",
            marginBottom: "-2px",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}
        >
          Activity
          {activities.length > 0 && (
            <span style={{
              backgroundColor: "#2563eb",
              color: "white",
              borderRadius: "10px",
              padding: "0.125rem 0.5rem",
              fontSize: "0.75rem",
              fontWeight: "600"
            }}>
              {activities.length}
            </span>
          )}
        </button>
      </div>

      {/* Tab Content - Scrollable */}
      <div style={{ 
        flex: 1, 
        overflowY: "auto",
        overflowX: "hidden"
      }}>
        {activeTab === "details" ? (
          <div style={{
            display: "flex",
            flexDirection: "column",
            gap: "0"
          }}>
        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "0.5rem",
          padding: "0.5rem 0",
          borderBottom: "1px solid #f3f4f6"
        }}>
          <FaMapMarkerAlt size={18} color="#6b7280" />
          <div style={{ flex: 1 }}>
            <UiText variant="label" as="div" style={{ 
              fontSize: "0.75rem",
              color: "#9ca3af",
              marginBottom: "0.125rem"
            }}>
              Location
            </UiText>
            <UiText variant="small" as="div" style={{ 
              color: "#111827",
              fontWeight: "500"
            }}>
              {company.location || "Not specified"}
            </UiText>
          </div>
        </div>

        <div style={{ 
          display: "flex", 
          alignItems: "center", 
          gap: "0.5rem",
          padding: "0.5rem 0",
          borderBottom: "1px solid #f3f4f6"
        }}>
          <FaGlobe size={18} color="#6b7280" />
          <div style={{ flex: 1 }}>
            <UiText variant="label" as="div" style={{ 
              fontSize: "0.75rem",
              color: "#9ca3af",
              marginBottom: "0.125rem"
            }}>
              Website
            </UiText>
            {company.website ? (
              <UiText variant="small" as="a" 
                href={company.website}
                target="_blank"
                rel="noreferrer"
                style={{ 
                  color: "#2563eb",
                  fontWeight: "500",
                  textDecoration: "none"
                }}
              >
                {company.website}
              </UiText>
            ) : (
              <UiText variant="small" as="div" style={{ 
                color: "#111827",
                fontWeight: "500"
              }}>
                Not provided
              </UiText>
            )}
          </div>
        </div>

        <div 
          onClick={handleCustomersClick}
          style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.5rem",
            padding: "0.5rem 0",
            borderBottom: "1px solid #f3f4f6",
            cursor: onViewCustomers ? "pointer" : "default",
            transition: "background-color 0.2s"
          }}
          onMouseOver={(e) => {
            if (onViewCustomers) e.currentTarget.style.backgroundColor = "#f9fafb";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <FaUsers size={18} color="#6b7280" />
          <div style={{ flex: 1 }}>
            <UiText variant="label" as="div" style={{ 
              fontSize: "0.75rem",
              color: "#9ca3af",
              marginBottom: "0.125rem"
            }}>
              Customers
            </UiText>
            <UiText variant="small" as="div" style={{ 
              color: "#111827",
              fontWeight: "500"
            }}>
              {company.customers?.length || 0}
            </UiText>
          </div>
        </div>

        <div 
          onClick={handleDealsClick}
          style={{ 
            display: "flex", 
            alignItems: "center", 
            gap: "0.5rem",
            padding: "0.5rem 0",
            borderBottom: "1px solid #f3f4f6",
            cursor: onViewDeals ? "pointer" : "default",
            transition: "background-color 0.2s"
          }}
          onMouseOver={(e) => {
            if (onViewDeals) e.currentTarget.style.backgroundColor = "#f9fafb";
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.backgroundColor = "transparent";
          }}
        >
          <FaBriefcase size={18} color="#6b7280" />
          <div style={{ flex: 1 }}>
            <UiText variant="label" as="div" style={{ 
              fontSize: "0.75rem",
              color: "#9ca3af",
              marginBottom: "0.125rem"
            }}>
              Deals
            </UiText>
            <UiText variant="small" as="div" style={{ 
              color: "#111827",
              fontWeight: "500"
            }}>
              {company.deals_count || 0}
            </UiText>
          </div>
        </div>

        {company.description && (
          <div style={{ 
            display: "flex", 
            alignItems: "flex-start", 
            gap: "0.5rem",
            padding: "0.5rem 0",
            borderBottom: "1px solid #f3f4f6"
          }}>
            <FaBuilding size={18} color="#6b7280" style={{ marginTop: "2px" }} />
            <div style={{ flex: 1 }}>
              <UiText variant="label" as="div" style={{ 
                fontSize: "0.75rem",
                color: "#9ca3af",
                marginBottom: "0.125rem"
              }}>
                Description
              </UiText>
              <UiText variant="small" as="div" style={{ 
                color: "#111827",
                fontWeight: "500",
                lineHeight: "1.5"
              }}>
                {company.description}
              </UiText>
            </div>
          </div>
        )}
          </div>
        ) : (
          /* Activity Tab */
          <div style={{ display: "flex", flexDirection: "column", height: "100%", gap: "1rem", minHeight: 0 }}>
            {/* Add Activity Button */}
            <div>
              <button
                onClick={() => setShowActivityModal(true)}
                style={{
                  width: "100%",
                  padding: "0.75rem",
                  background: "#2563eb",
                  color: "white",
                  border: "none",
                  borderRadius: "10px",
                  fontSize: "0.875rem",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "0.5rem",
                  transition: "all 0.2s",
                  boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)"
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.transform = "translateY(-2px)";
                  e.currentTarget.style.boxShadow = "0 6px 16px rgba(37, 99, 235, 0.4)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = "translateY(0)";
                  e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.3)";
                }}
              >
                <Plus size={18} />
                Log Activity
              </button>
            </div>

            {/* Activity Timeline */}
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              <RecentActivityTimeline 
                activities={activities} 
                loading={loadingActivities}
                onEdit={handleEditActivity}
                currentUserId={user?.id}
                isAdmin={user?.role === "admin"}
              />
            </div>
          </div>
        )}
      </div>

      {/* Action Buttons - Fixed at bottom when on Details tab */}
      {activeTab === "details" && canEditCompany(company) && (
        <div style={{ display: "flex", gap: "0.5rem", paddingTop: "0.75rem", borderTop: "2px solid #f3f4f6" }}>
          <button
            onClick={() => onEdit(company)}
            style={{
              flex: 1,
              background: "#2563eb",
              color: "white",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "0.95rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(37, 99, 235, 0.3)"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(37, 99, 235, 0.4)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(37, 99, 235, 0.3)";
            }}
          >
            <FaEdit size={16} /> Edit
          </button>
          <button
            onClick={() => onDelete(company)}
            style={{
              flex: 1,
              background: "#dc2626",
              color: "white",
              padding: "8px 12px",
              borderRadius: "8px",
              border: "none",
              cursor: "pointer",
              fontWeight: "600",
              fontSize: "0.95rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "0.5rem",
              transition: "all 0.2s",
              boxShadow: "0 4px 12px rgba(245, 87, 108, 0.3)"
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.transform = "translateY(-2px)";
              e.currentTarget.style.boxShadow = "0 6px 16px rgba(245, 87, 108, 0.4)";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.transform = "translateY(0)";
              e.currentTarget.style.boxShadow = "0 4px 12px rgba(245, 87, 108, 0.3)";
            }}
          >
            <FaTrash size={16} /> Delete
          </button>
        </div>
      )}

      {/* Add Activity Modal */}
      <AddActivityModal
        isOpen={showActivityModal}
        onClose={() => {
          setShowActivityModal(false);
          setEditingActivity(null);
        }}
        onSubmit={handleSaveActivity}
        companyName={company.name}
        activity={editingActivity}
      />
    </div>
  );
}
